<template>
  <AppMenuAsPopover offset-x
                    top
                    :card-min-width="330"
                    :card-max-width="330"
                    @open="onMenuOpen"
  >
    <template #activator="{ props }">
      <AppButton block
                 color="#eee"
                 rounded
                 v-bind="props"
      >
        <font-awesome-icon :icon="['far', 'plus']"
                           class="mr-4"
        ></font-awesome-icon>
        {{ $t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.createStatus') }}
      </AppButton>
    </template>
    <template #default="{closeMenu}">
      <v-card-title class="newStatus__card__title text-body-1 font-weight-medium d-flex align-center justify-space-between">
        {{ $t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.createStatus') }}
        <AppButton icon
                   @click="closeMenu"
        >
          <font-awesome-icon :icon="['fal', 'xmark']"></font-awesome-icon>
        </AppButton>
      </v-card-title>
      <v-card-text class="text--primary pt-4">
        <v-row>
          <v-col>
            <div class="mb-4 font-weight-medium">{{$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.form.statusNameLabel')}}</div>
            <AppTextField v-model="v$.newStatus.label.$model"
                          class="required"
                          :error-messages="labelErrors"
                          :placeholder="$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.form.statusNamePlaceholder')"
                          :counter="45"
                          :maxlength="45"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <div class="mb-4 font-weight-medium">{{$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.form.statusCategoryLabel')}}</div>
            <AppSelect v-model="v$.newStatus.category.$model"
                       :items="taskStatusCategoriesList"
                       item-value="name"
                       :error-messages="categoryErrors"
                       class="required"
            >
              <template #item="{item, props}">
                <v-list-item v-bind="props">
                  <template #title>
                    {{ $t(`common.statusCategoryNames.${item.raw.name}`) }}
                  </template>
                </v-list-item>
              </template>
              <template #selection="{item}">
                {{ $t(`common.statusCategoryNames.${item.raw.name}`) }}
              </template>
            </AppSelect>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <div class="mb-4 font-weight-medium">{{$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.form.statusColorLabel')}}</div>
            <ChecklistStatusDialogColorPicker v-model="v$.newStatus.color.$model"/>
            <span v-if="colorErrors.length"
                  class="text-caption text-error">
              {{ colorErrors[0] }}
            </span>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="text-right">
            <AppButton color="white"
                       class="mr-4"
                       :disabled="postStatusPending"
                       @click="closeMenu"
            >
              {{ $t('common.cancel') }}
            </AppButton>
            <AppButton color="primary"
                       :loading="postStatusPending"
                       @click="preparePostStatus(closeMenu)"
            >
              {{ $t('common.register') }}
            </AppButton>
          </v-col>
        </v-row>
      </v-card-text>
    </template>
  </AppMenuAsPopover>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { cloneDeep } from 'lodash-es'
import { mapActions, mapGetters, mapState } from 'vuex'

import AppMenuAsPopover from '@/common/AppMenuAsPopover.vue'
import AppSelect from '@/common/AppSelect.vue'
import AppTextField from '@/common/AppTextField.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import ChecklistStatusDialogColorPicker from '@/project/checklist/dialogs/ChecklistStatusDialogColorPicker.vue'
import { POST_STATUS } from '@/store/modules/checklist/action_types'


export default {
  name: 'ChecklistStatusDialogNewStatus',
  components: { ChecklistStatusDialogColorPicker, AppSelect, AppTextField, AppMenuAsPopover, AppButton },
  setup: () => ({ v$: useVuelidate() }),
  props: {
    category: {
      type: String,
      required: true,
    },
  },
  validations: {
    newStatus: {
      label: { required },
      category: { required },
      color: { required },
    },
  },
  data () {
    return {
      newStatus: {
        label: '',
        category: this.category,
        color: '',
      },
    }
  },
  computed: {
    ...mapState('checklist', ['postStatusPending', 'taskStatusCategories']),
    ...mapGetters('checklist', ['taskStatusCategoriesList']),
    ...mapGetters('room', ['roomMnemo']),
    labelErrors () {
      return formFieldErrors(
        this.v$.newStatus.label,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.form.statusNameLabel') })],
        ],
      )
    },
    categoryErrors () {
      return formFieldErrors(
        this.v$.newStatus.category,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.form.statusCategoryLabel') })],
        ],
      )
    },
    colorErrors () {
      return formFieldErrors(
        this.v$.newStatus.color,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.form.statusColorLabel') })],
        ],
      )
    },
  },
  methods: {
    ...mapActions('checklist', [POST_STATUS]),
    onMenuOpen () {
      this.v$.$reset()
      this.newStatus = {
        label: '',
        category: this.category,
        color: '',
      }
    },
    async preparePostStatus (closeMenuFunction) {
      this.v$.$touch()
      if (!this.v$.$invalid) {
        const statusCategoriesToPost = cloneDeep(this.taskStatusCategories)
        statusCategoriesToPost[this.newStatus.category].unshift({
          label: this.newStatus.label,
          color: this.newStatus.color,
        })

        try {
          await this.POST_STATUS({
            mnemo: this.roomMnemo,
            data: { statuses: statusCategoriesToPost },
          })
        } catch (error) {
          console.error(error)
          toast.error(this.$t('project.checklist.dialogs.ChecklistStatusDialogNewStatus.postStatusError'))
        } finally {
          closeMenuFunction()
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
.newStatus {
  &__card {
    &__title {
      border-bottom: 1px solid rgb(var(--v-theme-grey-lighten-1));
    }
  }
}
:deep(label.v-label.v-field-label){
  display: none !important
}
</style>
